import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FacilitySelectorService, FacilitySearchParms } from '../../services/facility-selector.service';

import { Subject, Observable, combineLatest } from "rxjs";
import { map, switchMap, startWith, debounceTime, distinctUntilChanged } from "rxjs/operators";

import * as _ from "lodash-es";

export interface Location {
  locationNumber: string;
  locationName: string;
}

export interface SearchData {
  searchType: string;
  selectorDataSource: string;
}

@Component({
  selector: 'velocity-facility-selector-doa;pg',
  templateUrl: 'facility-selector-search.dialog.html',
  styleUrls: ['facility-selector-search.dialog.scss']
})
export class FacilitySelectorSearchDialog implements OnInit {

  private selectedValue: string;

  private loadingRegion = true;
  private regionOnChange: Subject<string>;
  private regions: Observable<Location[]>;
  private searchTextRegion: string;

  private loadingArea = true;
  private areaOnChange: Subject<string>;
  private areas: Observable<Location[]>;
  private searchTextArea: string;

  private loadingDistrict = true;
  private districtOnChange: Subject<string>;
  private districts: Observable<Location[]>;
  private searchTextDistrict: string;

  private loadingFacility = true;
  private facilityOnChange: Subject<string>;
  private facilities: Observable<Location[]>;
  private searchTextFacility: string;

  onCancelClick(): void {
    this.dialogRef.close();
  }

  public doChangeRegion(newText: string) {
    this.searchTextRegion = newText;
    if (this.regionOnChange)
      this.regionOnChange.next(newText);
  }

  public doChangeArea(newText: string) {
    this.searchTextArea = newText;
    if (this.areaOnChange)
      this.areaOnChange.next(newText);
  }

  public doChangeDistrict(newText: string) {
    this.searchTextDistrict = newText;
    if (this.districtOnChange)
      this.districtOnChange.next(newText);
  }

  public doChangeFacility(newText: string) {
    this.searchTextFacility = newText;
    if (this.facilityOnChange)
      this.facilityOnChange.next(newText);
  }

  ngOnInit(): void {
    this.regionOnChange = new Subject<string>();
    this.areaOnChange = new Subject<string>();
    this.districtOnChange = new Subject<string>();
    this.facilityOnChange = new Subject<string>();

    this.regions = this.regionOnChange
      .pipe(
        startWith(""),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(
          (searchText) => {
            this.loadingRegion = true;

            const searchParms = new FacilitySearchParms();

            if (/^\d\d\d\d\d$/g.test(searchText)) {
              searchParms.RegionId = searchText;
            } else {
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.RegionSearch = searchText;
            }

            const result = this.facilitySelectorService.getFacilities(this.data.selectorDataSource, 'region', searchParms);

            return result;
          }
        ),
        map(facilities => {
          this.loadingRegion = false;
          return _.map(facilities, (facility) => {
            return {
              locationName: facility.regionName,
              locationNumber: facility.regionNumber
            } as Location
          });
        })
      );

    if (this.data.searchType === 'region') {
      this.regionOnChange
        .subscribe(searchText => {
          const searchParms = new FacilitySearchParms();

          if (/^\d\d\d\d\d$/g.test(searchText)) {
            searchParms.RegionId = searchText;

            this.facilitySelectorService
              .getFacilities(this.data.selectorDataSource, 'region', searchParms)
              .subscribe(result => {
                if (result.length === 1)
                  this.selectedValue = searchText
                else
                  this.selectedValue = "";
              });
          } else {
            this.selectedValue = "";
          }
        })
    }

    this.areas =
      combineLatest(
        this.regionOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.areaOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged())
      ).pipe(
        switchMap(
          ([region, area]) => {
            this.loadingArea = true;

            const searchParms = new FacilitySearchParms();

            if (/^\d\d\d\d\d$/g.test(region)) {
              searchParms.LimitByRegionId = region;
            }

            if (/^\d\d\d\d\d$/g.test(area)) {
              searchParms.AreaId = area;
            } else {
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.RegionSearch = area;
            }

            const result = this.facilitySelectorService.getFacilities(this.data.selectorDataSource, 'area', searchParms);

            return result;
          }
        ),
        map(facilities => {
          this.loadingArea = false;
          return _.map(facilities, (facility) => {
            return {
              locationName: facility.areaName,
              locationNumber: facility.areaNumber
            } as Location
          });
        })
      );

    if (this.data.searchType === 'area') {
      this.areaOnChange
        .subscribe(searchText => {
          const searchParms = new FacilitySearchParms();

          if (/^\d\d\d\d\d$/g.test(searchText)) {
            searchParms.AreaId = searchText;

            this.facilitySelectorService
              .getFacilities(this.data.selectorDataSource, 'area', searchParms)
              .subscribe(result => {
                if (result.length === 1)
                  this.selectedValue = searchText
                else
                  this.selectedValue = "";
              });
          } else {
            this.selectedValue = "";
          }
        })
    }

    this.districts =
      combineLatest(
        this.regionOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.areaOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.districtOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged())
      ).pipe(
        switchMap(
          ([region, area, district]) => {
            this.loadingDistrict = true;

            const searchParms = new FacilitySearchParms();

            if (/^\d\d\d\d\d$/g.test(region)) {
              searchParms.LimitByRegionId = region;
            }

            if (/^\d\d\d\d\d$/g.test(area)) {
              searchParms.LimitByAreaId = area;
            }

            if (/^\d\d\d\d\d$/g.test(district)) {
              searchParms.DistrictId = district;
            } else {
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.DistrictSearch = district;
            }

            const result = this.facilitySelectorService.getFacilities(this.data.selectorDataSource, 'district', searchParms);

            return result;
          }
        ),
        map(facilities => {
          this.loadingDistrict = false;
          return _.map(facilities, (facility) => {
            return {
              locationName: facility.districtName,
              locationNumber: facility.districtNumber
            } as Location
          });
        })
      );

    if (this.data.searchType === 'district') {
      this.districtOnChange
        .subscribe(searchText => {
          const searchParms = new FacilitySearchParms();

          if (/^\d\d\d\d\d$/g.test(searchText)) {
            searchParms.DistrictId = searchText;

            this.facilitySelectorService
              .getFacilities(this.data.selectorDataSource, 'district', searchParms)
              .subscribe(result => {
                if (result.length === 1)
                  this.selectedValue = searchText
                else
                  this.selectedValue = "";
              });
          } else {
            this.selectedValue = "";
          }
        })
    }

    this.facilities =
      combineLatest(
        this.regionOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.areaOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.districtOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged()),
        this.facilityOnChange.pipe(startWith(""), debounceTime(300), distinctUntilChanged())
      ).pipe(
        switchMap(
          ([region, area, district, facility]) => {
            this.loadingFacility = true;

            const searchParms = new FacilitySearchParms();

            if (/^\d\d\d\d\d$/g.test(region)) {
              searchParms.LimitByRegionId = region;
            }

            if (/^\d\d\d\d\d$/g.test(area)) {
              searchParms.LimitByAreaId = area;
            }

            if (/^\d\d\d\d\d$/g.test(district)) {
              searchParms.LimitByDistrictId = district;
            }

            if (/^\d\d\d\d\d$/g.test(facility)) {
              searchParms.FacilityId = facility;
            } else {
              searchParms.Page = 1;
              searchParms.PageSize = 50;
              searchParms.FacilitySearch = facility;
            }

            const result = this.facilitySelectorService.getFacilities(this.data.selectorDataSource, 'facility', searchParms);

            return result;
          }
        ),
        map(facilities => {
          this.loadingFacility = false;
          return _.map(facilities, (facility) => {
            return {
              locationName: facility.facilityName,
              locationNumber: facility.facilityNumber
            } as Location
          });
        })
      );

    if (this.data.searchType === 'facility') {
      this.facilityOnChange
        .subscribe(searchText => {
          const searchParms = new FacilitySearchParms();

          if (/^\d\d\d\d\d$/g.test(searchText)) {
            searchParms.FacilityId = searchText;

            this.facilitySelectorService
              .getFacilities(this.data.selectorDataSource, 'facility', searchParms)
              .subscribe(result => {
                if (result.length === 1)
                  this.selectedValue = searchText
                else
                  this.selectedValue = "";
              });
          } else {
            this.selectedValue = "";
          }
        })
    }
  }

  constructor(
    public dialogRef: MatDialogRef<FacilitySelectorSearchDialog>,
    @Inject(MAT_DIALOG_DATA) public data: SearchData,
    private facilitySelectorService: FacilitySelectorService
  ) { }

  onNoClick(): void {
    this.dialogRef.close();
  }

  getDisplayType(): string {
    if (this.data.searchType === "region") {
      return "Region";
    } else if (this.data.searchType === "area") {
      return "Area";
    } else if (this.data.searchType === "district") {
      return "District";
    } else if (this.data.searchType === "facility") {
      return "Facility";
    } else {
      return "";
    }
  }
}
